import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as api from '../../services/api';

const Page = styled.div`
  padding: 24px;
  max-width: 960px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  color: #6b7280;
  margin: 0;
  font-size: 14px;
  max-width: 36rem;
  line-height: 1.5;
`;

const AddBtn = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: 10px;
  color: #fff;
  background: linear-gradient(135deg, #0ea5e9 0%, #2563eb 55%, #4f46e5 100%);
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }
  &:disabled {
    opacity: 0.65;
    cursor: wait;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const Card = styled.button`
  text-align: left;
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #fff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
  cursor: pointer;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  &:hover {
    border-color: #93c5fd;
    box-shadow: 0 8px 24px rgba(37, 99, 235, 0.12);
  }
`;

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.45;
`;

const Err = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 14px;
  margin-bottom: 16px;
`;

const Empty = styled.p`
  color: #64748b;
  font-size: 14px;
`;

const ResearchPortalPage: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<api.ResearchProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await api.listResearchProjects();
      setProjects(res.data?.projects || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAdd = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await api.createResearchProject({});
      const id = res.data?.project?.id;
      if (id) navigate(`/research/portal/${id}`);
      else setError('Created but no project id returned');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Page>
      <HeaderRow>
        <div>
          <Title>Research Portal</Title>
          <Subtitle>
            Super-admin workspace for research project configs (JSON), including budget templates. Each project has a
            stable UUID; configs are stored in PostgreSQL (RDS) for fast load and save.
          </Subtitle>
        </div>
        <AddBtn type="button" onClick={() => void handleAdd()} disabled={creating}>
          {creating ? 'Creating…' : '+ Add research project'}
        </AddBtn>
      </HeaderRow>

      {error && <Err>{error}</Err>}

      {loading ? (
        <Empty>Loading…</Empty>
      ) : projects.length === 0 ? (
        <Empty>No projects yet. Use the button above to create one from the default template.</Empty>
      ) : (
        <CardGrid>
          {projects.map((p) => (
            <Card key={p.id} type="button" onClick={() => navigate(`/research/portal/${p.id}`)}>
              <CardTitle>{p.title?.trim() || 'Untitled project'}</CardTitle>
              <CardMeta>
                <div>ID: {p.id}</div>
                <div>Updated: {new Date(p.updated_at).toLocaleString()}</div>
              </CardMeta>
            </Card>
          ))}
        </CardGrid>
      )}
    </Page>
  );
};

export default ResearchPortalPage;
